import React, { useState, useEffect } from 'react';
import api from '../api';
import CollapsibleFilters from './CollapsibleFilters';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface Warehouse {
  id: number;
  code: string;
  name: string;
  is_default?: boolean;
}

interface GoodsReceipt {
  id: number;
  receipt_number: string;
  warehouse_id: number;
  supplier_name: string;
  supplier_reference?: string;
  receipt_date: string;
  status: string;
  parent_grn_id?: number;
  items: GoodsReceiptItem[];
}

interface GoodsReceiptItem {
  product_id: number;
  expected_quantity: number;
  received_quantity: number;
  damaged_quantity: number;
  unit_cost: number;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  attributes?: Array<{ attribute_name: string; value: string }>;
}

const GoodsReceiptManager: React.FC<{ userRole?: string }> = ({ userRole = 'viewer' }) => {
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [viewingReceipt, setViewingReceipt] = useState<GoodsReceipt | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('draft,received,partial');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Helper: format product label as "SKU - Name - Color - Decoration"
  const getProductLabel = (product: Product) => {
    const color = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value || '';
    const decoration = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || '';
    let label = `${product.sku} - ${product.name}`;
    if (color) label += ` - ${color}`;
    if (decoration) label += ` - ${decoration}`;
    return label;
  };
  
  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchText]);
  
  const [formData, setFormData] = useState({
    warehouse_id: 0,
    supplier_name: '',
    supplier_reference: '',
    receipt_date: new Date().toISOString().split('T')[0],
    notes: '',
    items: [] as GoodsReceiptItem[]
  });
  const [followUpParentId, setFollowUpParentId] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<{[key: string]: string[]}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supplierVendorResults, setSupplierVendorResults] = useState<Array<{id: number; name: string}>>([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, debouncedSearch, dateFrom, dateTo, filterStatus]);

  useEffect(() => {
    fetchWarehousesAndProducts();
  }, []);

  const fetchWarehousesAndProducts = async () => {
    try {
      const [warehousesRes, productsRes] = await Promise.all([
        api.get('/inventory/warehouses/'),
        api.get('/products/', { params: { limit: 500 } })
      ]);
      setWarehouses(warehousesRes.data);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error fetching warehouses/products:', error);
    }
  };

  const fetchData = async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      const params: any = { skip, limit: pageSize };
      if (debouncedSearch.trim()) {
        params.q = debouncedSearch.trim();
      }
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const receiptsRes = await api.get('/inventory/goods-receipts/', { params });
      setReceipts(receiptsRes.data);
      setTotalCount(receiptsRes.data.length);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search is now handled server-side, use receipts directly

  const [itemSearchTexts, setItemSearchTexts] = useState<string[]>([]);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { 
        product_id: 0, 
        expected_quantity: 0, 
        received_quantity: 0, 
        damaged_quantity: 0, 
        unit_cost: 0 
      }]
    });
    setItemSearchTexts([...itemSearchTexts, '']);
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
    clearFormErrors(); // Clear errors when user makes changes
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
    setItemSearchTexts(itemSearchTexts.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const errors: {[key: string]: string[]} = {};
    
    // Validate warehouse
    if (!formData.warehouse_id) {
      errors.warehouse_id = ['Please select a warehouse'];
    }
    
    // Validate supplier name
    if (!formData.supplier_name || formData.supplier_name.trim().length < 2) {
      errors.supplier_name = ['Supplier name must be at least 2 characters'];
    }
    
    // Validate receipt date
    if (formData.receipt_date) {
      const receiptDate = new Date(formData.receipt_date);
      const today = new Date();
      const futureLimit = new Date();
      futureLimit.setDate(today.getDate() + 30);
      
      if (receiptDate > futureLimit) {
        errors.receipt_date = ['Receipt date cannot be more than 30 days in the future'];
      }
    }
    
    // Validate items
    if (!formData.items || formData.items.length === 0) {
      errors.items = ['At least one item is required'];
    } else {
      const itemErrors: string[] = [];
      const productIds = new Set();
      
      formData.items.forEach((item, index) => {
        const itemPrefix = `Item ${index + 1}: `;
        
        // Check for duplicate products
        if (productIds.has(item.product_id)) {
          itemErrors.push(`${itemPrefix}Duplicate product not allowed`);
        } else {
          productIds.add(item.product_id);
        }
        
        // Validate product selection
        if (!item.product_id) {
          itemErrors.push(`${itemPrefix}Please select a product`);
        }
        
        // Validate quantities
        if (item.expected_quantity <= 0) {
          itemErrors.push(`${itemPrefix}Expected quantity must be greater than 0`);
        }
        
        if (item.received_quantity < 0) {
          itemErrors.push(`${itemPrefix}Received quantity cannot be negative`);
        }
        
        if (item.damaged_quantity < 0) {
          itemErrors.push(`${itemPrefix}Damaged/Short quantity cannot be negative`);
        }
        
        if (item.received_quantity + item.damaged_quantity > item.expected_quantity * 1.1) {
          itemErrors.push(`${itemPrefix}Total received + damaged/short exceeds expected by more than 10%`);
        }
        
        // Validate unit cost
        if (item.unit_cost < 0) {
          itemErrors.push(`${itemPrefix}Unit cost cannot be negative`);
        }
      });
      
      if (itemErrors.length > 0) {
        errors.items = itemErrors;
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Format the data for submission - convert date string to ISO datetime
      const submitData: any = {
        ...formData,
        receipt_date: formData.receipt_date ? new Date(formData.receipt_date).toISOString() : new Date().toISOString()
      };
      if (followUpParentId) {
        submitData.parent_grn_id = followUpParentId;
        // Follow-up GRNs go directly to "received" status so inventory auto-updates
        submitData.status = 'received';
      }
      
      await api.post('/inventory/goods-receipts/', submitData);
      alert('Goods receipt created successfully');
      setShowForm(false);
      setFollowUpParentId(null);
      setFormData({
        warehouse_id: 0,
        supplier_name: '',
        supplier_reference: '',
        receipt_date: new Date().toISOString().split('T')[0],
        notes: '',
        items: []
      });
      setFormErrors({});
      fetchData();
    } catch (error: any) {
      console.error('Error creating receipt:', error);
      
      // Handle validation errors from backend
      if (error.response?.status === 422) {
        console.error('Validation error details:', error.response?.data);
        const detail = error.response?.data?.detail;
        
        // Check if it's a Pydantic validation error
        if (Array.isArray(detail)) {
          const errors: {[key: string]: string[]} = {};
          detail.forEach((err: any) => {
            const field = err.loc?.join('.') || 'general';
            if (!errors[field]) {
              errors[field] = [];
            }
            errors[field].push(err.msg || 'Validation error');
          });
          setFormErrors(errors);
          alert('Please fix the validation errors and try again.');
        } else if (detail?.errors) {
          setFormErrors(detail.errors);
          alert('Please fix the validation errors and try again.');
        } else {
          alert(typeof detail === 'string' ? detail : 'Validation error. Please check your inputs.');
        }
      } else if (error.response?.status === 400 && error.response?.data?.detail?.errors) {
        setFormErrors(error.response.data.detail.errors);
      } else {
        const errorMsg = error.response?.data?.detail?.summary || 
                        error.response?.data?.detail || 
                        'Failed to create goods receipt';
        alert(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFieldError = (fieldName: string) => {
    if (formErrors[fieldName]) {
      return (
        <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
          {formErrors[fieldName].map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      );
    }
    return null;
  };

  const getFieldStyle = (fieldName: string) => {
    const baseStyle = {
      width: '100%',
      padding: '0.5rem',
      borderRadius: '4px',
      border: '1px solid #cbd5e1'
    };
    
    if (formErrors[fieldName]) {
      return {
        ...baseStyle,
        border: '1px solid #ef4444',
        backgroundColor: '#fef2f2'
      };
    }
    
    return baseStyle;
  };

  const clearFormErrors = () => {
    setFormErrors({});
  };

  const searchSupplierVendors = async (q: string) => {
    if (!q.trim() || q.trim().length < 2) { setSupplierVendorResults([]); setShowSupplierDropdown(false); return; }
    try {
      const res = await api.get('/vendors/', { params: { q: q.trim(), limit: 10 } });
      const results = Array.isArray(res.data) ? res.data : [];
      setSupplierVendorResults(results);
      setShowSupplierDropdown(results.length > 0);
    } catch (e) { setSupplierVendorResults([]); setShowSupplierDropdown(false); }
  };

  const handleDelete = async (id: number) => {
    const receipt = receipts.find(r => r.id === id);
    setDeleteConfirm({show: true, id, name: receipt?.receipt_number || `GRN #${id}`});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/inventory/goods-receipts/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchData();
    } catch (error: any) {
      console.error('Error deleting receipt:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete receipt';
      alert(errorMsg);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedReceipts = [...receipts].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = '', bVal: any = '';
    switch (sortField) {
      case 'receipt_number': aVal = a.receipt_number || ''; bVal = b.receipt_number || ''; break;
      case 'warehouse': aVal = warehouses.find(w => w.id === a.warehouse_id)?.name || ''; bVal = warehouses.find(w => w.id === b.warehouse_id)?.name || ''; break;
      case 'supplier_name': aVal = a.supplier_name || ''; bVal = b.supplier_name || ''; break;
      case 'supplier_reference': aVal = a.supplier_reference || ''; bVal = b.supplier_reference || ''; break;
      case 'receipt_date': aVal = a.receipt_date || ''; bVal = b.receipt_date || ''; break;
      case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
      default: return 0;
    }
    if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(receipts.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length > 1) {
      setShowBulkDeleteConfirm(true);
    } else if (selectedIds.length === 1) {
      handleDelete(selectedIds[0]);
      setSelectedIds([]);
    }
  };

  const confirmBulkDelete = async () => {
    if (confirmText !== 'confirm') {
      return;
    }

    const errors: string[] = [];
    for (const id of selectedIds) {
      try {
        await api.delete(`/inventory/goods-receipts/${id}`);
      } catch (error: any) {
        const errorMsg = error.response?.data?.detail || 'Failed to delete';
        errors.push(`Receipt ${id}: ${errorMsg}`);
      }
    }

    if (errors.length > 0) {
      alert(`Some deletions failed:\n${errors.join('\n')}`);
    }

    setSelectedIds([]);
    setShowBulkDeleteConfirm(false);
    setConfirmText('');
    fetchData();
  };

  const handleView = async (id: number) => {
    try {
      const response = await api.get(`/inventory/goods-receipts/${id}`);
      setViewingReceipt(response.data);
    } catch (error) {
      console.error('Error fetching receipt details:', error);
      alert('Failed to load receipt details');
    }
  };

  const [pendingStatusChanges, setPendingStatusChanges] = useState<Record<number, string>>({});

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await api.put(`/inventory/goods-receipts/${id}`, { status: newStatus });
      setPendingStatusChanges(prev => { const n = {...prev}; delete n[id]; return n; });
      fetchData();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleReprocessInventory = async (id: number) => {
    if (!window.confirm('This will update inventory stock for this completed GRN. Continue?')) return;
    try {
      const response = await api.post(`/inventory/goods-receipts/${id}/reprocess-inventory`);
      alert(response.data.message || 'Inventory reprocessed successfully');
      fetchData();
    } catch (error: any) {
      console.error('Error reprocessing inventory:', error);
      alert(error.response?.data?.detail || 'Failed to reprocess inventory');
    }
  };

  const handleCreateFollowUpGrn = (parentReceipt: GoodsReceipt) => {
    // Calculate remaining quantities per item (remaining = expected - received)
    const remainingItems = parentReceipt.items
      .map(item => {
        const remaining = item.expected_quantity - (item.received_quantity || 0);
        return remaining > 0 ? {
          product_id: item.product_id,
          expected_quantity: remaining,
          received_quantity: remaining,
          damaged_quantity: 0,
          unit_cost: 0
        } : null;
      })
      .filter(Boolean) as GoodsReceiptItem[];

    if (remainingItems.length === 0) {
      alert('All expected quantities have been received.');
      return;
    }

    // Determine root GRN id (in case this is already a follow-up)
    const rootGrnId = (parentReceipt as any).parent_grn_id || parentReceipt.id;

    // Pre-fill form with remaining quantities and link to parent
    const defaultWh = warehouses.find(w => w.is_default);
    setFormData({
      warehouse_id: parentReceipt.warehouse_id || (defaultWh ? defaultWh.id : 0),
      supplier_name: parentReceipt.supplier_name,
      supplier_reference: parentReceipt.supplier_reference || '',
      receipt_date: new Date().toISOString().split('T')[0],
      notes: `Follow-up for ${parentReceipt.receipt_number}`,
      items: remainingItems
    });
    setItemSearchTexts(
      remainingItems.map(item => {
        const product = products.find(p => p.id === item.product_id);
        return product ? getProductLabel(product) : '';
      })
    );
    setFollowUpParentId(rootGrnId);
    setShowForm(true);
  };

  const [editingReceipt, setEditingReceipt] = useState<GoodsReceipt | null>(null);
  const [editFormData, setEditFormData] = useState({
    warehouse_id: 0,
    supplier_name: '',
    supplier_reference: '',
    receipt_date: '',
    notes: '',
    items: [] as GoodsReceiptItem[]
  });
  const [editItemSearchTexts, setEditItemSearchTexts] = useState<string[]>([]);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const handleEditGrn = (receipt: GoodsReceipt) => {
    setEditingReceipt(receipt);
    setEditFormData({
      warehouse_id: receipt.warehouse_id,
      supplier_name: receipt.supplier_name,
      supplier_reference: receipt.supplier_reference || '',
      receipt_date: receipt.receipt_date ? receipt.receipt_date.split('T')[0] : '',
      notes: '',
      items: receipt.items || []
    });
    setEditItemSearchTexts(
      (receipt.items || []).map(item => {
        const product = products.find(p => p.id === item.product_id);
        return product ? getProductLabel(product) : '';
      })
    );
    setViewingReceipt(null);
  };

  const handleEditSubmit = async () => {
    if (!editingReceipt) return;
    setIsEditSubmitting(true);
    try {
      const hasReceivedQty = editFormData.items.some(i => (i.received_quantity || 0) > 0);
      const submitData: any = {
        warehouse_id: editFormData.warehouse_id,
        supplier_name: editFormData.supplier_name,
        supplier_reference: editFormData.supplier_reference || null,
        receipt_date: editFormData.receipt_date ? new Date(editFormData.receipt_date).toISOString() : null,
        status: editingReceipt.status === 'received' && hasReceivedQty ? 'received' : editingReceipt.status,
        items: editFormData.items
      };
      await api.put(`/inventory/goods-receipts/${editingReceipt.id}`, submitData);
      alert('GRN updated successfully');
      setEditingReceipt(null);
      fetchData();
    } catch (error: any) {
      console.error('Error updating GRN:', error);
      alert(error.response?.data?.detail || 'Failed to update GRN');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      draft: 'status-draft',
      received: 'status-active',
      partial: 'status-draft',
      completed: 'status-active',
      cancelled: 'status-inactive'
    };
    const partialStyle = status === 'partial' ? { background: '#fef3c7', color: '#92400e' } : {};
    return (
      <span className={`status-badge ${statusColors[status] || 'status-draft'}`} style={partialStyle}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const isPrivileged = userRole === 'admin' || userRole === 'manager';

  const getAvailableStatuses = (currentStatus: string): string[] => {
    if (isPrivileged) {
      // Admin/Manager can change to any status except back to draft
      switch (currentStatus) {
        case 'draft':
          return ['draft', 'received', 'cancelled'];
        default:
          return ['received', 'partial', 'completed', 'cancelled'].filter(s => s !== 'draft');
      }
    }
    // Other roles: forward-only transitions
    // Draft -> Received -> Completed; Cancelled only from Draft
    switch (currentStatus) {
      case 'draft':
        return ['draft', 'received', 'cancelled'];
      case 'received':
        return ['received', 'completed'];
      case 'partial':
        return ['partial']; // Cannot change — create follow-up GRN instead
      case 'completed':
        return ['completed']; // Cannot change
      case 'cancelled':
        return ['cancelled']; // Cannot change
      default:
        return [currentStatus];
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="card-title">Goods Receipt Notes</h2>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '200px' }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search receipt #, supplier, reference, status..."
            style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '400px' }}
          />
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => {
          const defaultWh = warehouses.find(w => w.is_default);
          setFormData(prev => ({ ...prev, warehouse_id: defaultWh ? defaultWh.id : 0 }));
          setShowForm(true);
        }}>
          <span className="btn-icon">➕</span>
          New Receipt
        </button>
      </div>

      <CollapsibleFilters label="Filters">
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Status</label>
            <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
              <option value="draft,received,partial">Open (Draft/Received/Partial)</option>
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="received">Received</option>
              <option value="partial">Partial</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>From Date</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setCurrentPage(1); }} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>To Date</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setCurrentPage(1); }} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
          </div>
          <button onClick={() => { setFilterStatus('draft,received,partial'); setDateFrom(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; }); setDateTo(new Date().toISOString().split('T')[0]); setSearchText(''); }} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>
            Reset
          </button>
        </div>
      </CollapsibleFilters>

      {selectedIds.length > 0 && (
        <div style={{ padding: '1rem 2rem', background: '#fef3c7', borderBottom: '1px solid #e2e8f0' }}>
          <span>{selectedIds.length} receipts selected</span>
          <button 
            className="btn btn-danger" 
            style={{ marginLeft: '1rem' }}
            onClick={handleBulkDeleteClick}
          >
            Delete Selected
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '50px' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedIds.length === receipts.length && receipts.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th onClick={() => handleSort('receipt_number')} style={{ cursor: 'pointer' }}>Receipt # {sortField === 'receipt_number' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('warehouse')} style={{ cursor: 'pointer' }}>Warehouse {sortField === 'warehouse' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('supplier_name')} style={{ cursor: 'pointer' }}>Supplier {sortField === 'supplier_name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('supplier_reference')} style={{ cursor: 'pointer' }}>Reference {sortField === 'supplier_reference' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('receipt_date')} style={{ cursor: 'pointer' }}>Date {sortField === 'receipt_date' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedReceipts.map(receipt => (
              <tr key={receipt.id} onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest('select') || target.closest('button') || target.closest('input[type="checkbox"]')) return;
                handleView(receipt.id);
              }} style={{ cursor: 'pointer' }}>
                <td>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={selectedIds.includes(receipt.id)}
                    onChange={(e) => handleSelectItem(receipt.id, e.target.checked)}
                  />
                </td>
                <td style={{ wordBreak: 'break-word' }}>{receipt.receipt_number}</td>
                <td style={{ wordBreak: 'break-word' }}>{warehouses.find(w => w.id === receipt.warehouse_id)?.name || '-'}</td>
                <td style={{ wordBreak: 'break-word' }}>{receipt.supplier_name}</td>
                <td style={{ wordBreak: 'break-word' }}>{receipt.supplier_reference || '-'}</td>
                <td>{new Date(receipt.receipt_date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                <td>
                  <select
                    value={pendingStatusChanges[receipt.id] ?? receipt.status}
                    onChange={(e) => setPendingStatusChanges(prev => ({...prev, [receipt.id]: e.target.value}))}
                    className="form-select"
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!isPrivileged && (receipt.status === 'completed' || receipt.status === 'cancelled' || receipt.status === 'partial')}
                  >
                    {getAvailableStatuses(receipt.status).map(status => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {pendingStatusChanges[receipt.id] && pendingStatusChanges[receipt.id] !== receipt.status && (
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '10px', padding: '3px 8px', whiteSpace: 'nowrap' }}
                        onClick={() => handleStatusChange(receipt.id, pendingStatusChanges[receipt.id])}
                      >
                        Submit
                      </button>
                    )}
                    {isPrivileged && receipt.status === 'completed' && (
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '10px', padding: '3px 8px', whiteSpace: 'nowrap' }}
                        onClick={() => handleReprocessInventory(receipt.id)}
                        title="Reprocess inventory if stock was not updated"
                      >
                        Reprocess
                      </button>
                    )}
                    {receipt.status === 'partial' && (
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '10px', padding: '3px 8px', whiteSpace: 'nowrap' }}
                        onClick={() => handleCreateFollowUpGrn(receipt)}
                        title="Create follow-up GRN for remaining quantity"
                      >
                        Follow-up
                      </button>
                    )}
                    <button 
                      className="btn btn-danger" 
                      onClick={() => handleDelete(receipt.id)}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {receipts.length > 0 && (
        <div style={{ 
          padding: '1rem 2rem', 
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} receipts
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', color: '#64748b', marginRight: '0.5rem' }}>
              Per page:
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                fontSize: '14px'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>

            <span style={{ fontSize: '14px', color: '#64748b', padding: '0 0.5rem' }}>
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage >= Math.ceil(totalCount / pageSize) ? 0.5 : 1,
                cursor: currentPage >= Math.ceil(totalCount / pageSize) ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '1200px', width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
            <h3>{followUpParentId ? 'Create Follow-up Goods Receipt' : 'Create Goods Receipt'}</h3>
            {followUpParentId && (
              <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '6px', fontSize: '13px', color: '#92400e', marginTop: '8px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px' }}>Follow-up for partially received GRN</div>
                {(() => {
                  const parentGrn = receipts.find(r => r.id === followUpParentId);
                  if (!parentGrn) return <div>Parent GRN #{followUpParentId}</div>;
                  return (
                    <div style={{ background: 'rgba(255,255,255,0.5)', borderRadius: '4px', padding: '8px', fontSize: '12px' }}>
                      <div className="form-grid-2" style={{ gap: '4px', marginBottom: '6px' }}>
                        <div><strong>GRN #:</strong> {parentGrn.receipt_number}</div>
                        <div><strong>Supplier:</strong> {parentGrn.supplier_name}</div>
                      </div>
                      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                            <th style={{ textAlign: 'left', padding: '4px' }}>SKU - Product</th>
                            <th style={{ textAlign: 'center', padding: '4px' }}>Expected</th>
                            <th style={{ textAlign: 'center', padding: '4px' }}>Received</th>
                            <th style={{ textAlign: 'center', padding: '4px' }}>Damaged/Short</th>
                            <th style={{ textAlign: 'center', padding: '4px' }}>Remaining</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parentGrn.items.map((item, idx) => {
                            const product = products.find(p => p.id === item.product_id);
                            const remaining = item.expected_quantity - (item.received_quantity || 0);
                            return (
                              <tr key={idx}>
                                <td style={{ padding: '4px' }}>{product ? getProductLabel(product) : `Product ${item.product_id}`}</td>
                                <td style={{ textAlign: 'center', padding: '4px' }}>{item.expected_quantity}</td>
                                <td style={{ textAlign: 'center', padding: '4px' }}>{item.received_quantity}</td>
                                <td style={{ textAlign: 'center', padding: '4px' }}>{item.damaged_quantity}</td>
                                <td style={{ textAlign: 'center', padding: '4px', fontWeight: 600, color: remaining > 0 ? '#dc2626' : '#16a34a' }}>{remaining}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}
            
            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1.5rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Warehouse *
                </label>
                <select
                  value={formData.warehouse_id}
                  onChange={(e) => {
                    setFormData({ ...formData, warehouse_id: parseInt(e.target.value) });
                    clearFormErrors();
                  }}
                  style={getFieldStyle('warehouse_id')}
                >
                  <option value={0}>Select Warehouse</option>
                  {warehouses.map(wh => (
                    <option key={wh.id} value={wh.id}>{wh.name}</option>
                  ))}
                </select>
                {renderFieldError('warehouse_id')}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Receipt Date *
                </label>
                <input
                  type="date"
                  value={formData.receipt_date}
                  onChange={(e) => {
                    setFormData({ ...formData, receipt_date: e.target.value });
                    clearFormErrors();
                  }}
                  max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  style={getFieldStyle('receipt_date')}
                />
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  Format: YYYY-MM-DD (e.g., 2026-02-08)
                </div>
                {renderFieldError('receipt_date')}
              </div>
            </div>

            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Supplier Name *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={formData.supplier_name}
                    onChange={(e) => {
                      setFormData({ ...formData, supplier_name: e.target.value });
                      clearFormErrors();
                      searchSupplierVendors(e.target.value);
                    }}
                    onFocus={() => { if (formData.supplier_name.trim().length >= 2) searchSupplierVendors(formData.supplier_name); }}
                    onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                    placeholder="Type to search vendors..."
                    style={getFieldStyle('supplier_name')}
                  />
                  {showSupplierDropdown && supplierVendorResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '160px', overflow: 'auto' }}>
                      {supplierVendorResults.map(v => (
                        <div
                          key={v.id}
                          onMouseDown={() => { setFormData({ ...formData, supplier_name: v.name }); setShowSupplierDropdown(false); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
                          onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                          onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          {v.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {renderFieldError('supplier_name')}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Supplier Reference (PO #)
                </label>
                <input
                  type="text"
                  value={formData.supplier_reference}
                  onChange={(e) => setFormData({ ...formData, supplier_reference: e.target.value })}
                  placeholder="PO-2024-001"
                  style={getFieldStyle('supplier_reference')}
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              />
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <label style={{ fontWeight: 600, fontSize: '16px' }}>Items *</label>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    addItem();
                    clearFormErrors();
                  }} 
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                >
                  + Add Item
                </button>
              </div>
              {renderFieldError('items')}

              <div className="line-items-scroll"><div>
              {/* Column Headers */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '3fr 1.2fr 1.2fr 1.2fr 80px', 
                gap: '0.75rem', 
                padding: '0.75rem',
                background: '#f1f5f9',
                borderRadius: '4px',
                marginBottom: '0.5rem',
                fontWeight: 600,
                fontSize: '13px',
                color: '#475569'
              }}>
                <div>Product</div>
                <div>Expected Qty</div>
                <div>Received Qty</div>
                <div>Damaged/Short Qty</div>
                
                <div style={{ textAlign: 'center' }}>Action</div>
              </div>

              {/* Items List */}
              {formData.items.length === 0 && (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center', 
                  color: '#94a3b8',
                  background: '#f8fafc',
                  borderRadius: '4px',
                  border: '2px dashed #cbd5e1'
                }}>
                  No items added yet. Click "Add Item" to get started.
                </div>
              )}

              {formData.items.map((item, index) => (
                <div key={index} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '3fr 1.2fr 1.2fr 1.2fr 80px', 
                  gap: '0.75rem', 
                  marginBottom: '0.75rem',
                  padding: '0.75rem',
                  background: '#ffffff',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    placeholder="Type SKU or product name..."
                    value={itemSearchTexts[index] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const newTexts = [...itemSearchTexts];
                      newTexts[index] = val;
                      setItemSearchTexts(newTexts);
                      const match = products.find(p => getProductLabel(p) === val || p.name === val || p.sku === val);
                      if (match) {
                        updateItem(index, 'product_id', match.id);
                        newTexts[index] = getProductLabel(match);
                        setItemSearchTexts([...newTexts]);
                      } else if (!val) {
                        updateItem(index, 'product_id', 0);
                      }
                    }}
                    list={`grn-products-${index}`}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <datalist id={`grn-products-${index}`}>
                    {products.map(p => <option key={p.id} value={getProductLabel(p)}>{getProductLabel(p)}</option>)}
                  </datalist>
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    value={item.expected_quantity || ''}
                    onChange={(e) => updateItem(index, 'expected_quantity', parseInt(e.target.value) || 0)}
                    style={{ 
                      padding: '0.6rem', 
                      borderRadius: '4px', 
                      border: '1px solid #cbd5e1', 
                      fontSize: '14px',
                      width: '100%'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    value={item.received_quantity || ''}
                    disabled={!followUpParentId}
                    onChange={(e) => {
                      const received = parseInt(e.target.value) || 0;
                      const newItems = [...formData.items];
                      newItems[index] = { ...newItems[index], received_quantity: received };
                      setFormData({ ...formData, items: newItems });
                    }}
                    style={{ 
                      padding: '0.6rem', 
                      borderRadius: '4px', 
                      border: '1px solid #cbd5e1', 
                      fontSize: '14px',
                      width: '100%',
                      background: !followUpParentId ? '#f1f5f9' : 'white',
                      cursor: !followUpParentId ? 'not-allowed' : 'text'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    value={item.damaged_quantity || ''}
                    disabled={!followUpParentId}
                    onChange={(e) => {
                      const newItems = [...formData.items];
                      newItems[index] = { ...newItems[index], damaged_quantity: parseInt(e.target.value) || 0 };
                      setFormData({ ...formData, items: newItems });
                    }}
                    style={{ 
                      padding: '0.6rem', 
                      borderRadius: '4px', 
                      border: '1px solid #cbd5e1', 
                      fontSize: '14px',
                      width: '100%',
                      background: !followUpParentId ? '#f1f5f9' : 'white',
                      cursor: !followUpParentId ? 'not-allowed' : 'text'
                    }}
                  />
                  <button 
                    onClick={() => removeItem(index)}
                    style={{ 
                      padding: '0.6rem', 
                      background: '#ef4444', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      width: '100%',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
                  >
                    ✕
                  </button>
                </div>
              ))}
              </div></div>
            </div>

            <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '1.5rem' }}>
              <button 
                className="btn btn-primary" 
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.6 : 1 }}
              >
                {isSubmitting ? 'Creating...' : 'Create Receipt'}
              </button>
              <button className="btn btn-secondary" onClick={() => {
                setShowForm(false);
                setFollowUpParentId(null);
                setFormData({
                  warehouse_id: 0,
                  supplier_name: '',
                  supplier_reference: '',
                  receipt_date: new Date().toISOString().split('T')[0],
                  notes: '',
                  items: []
                });
                setFormErrors({});
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Bulk Delete</h3>
            <p>You are about to delete {selectedIds.length} goods receipts. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirm"
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-danger"
                onClick={confirmBulkDelete}
                disabled={confirmText !== 'confirm'}
              >
                Delete Receipts
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowBulkDeleteConfirm(false);
                  setConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingReceipt && (
        <div className="modal-overlay" onClick={() => setViewingReceipt(null)}>
          <div className="modal" style={{ maxWidth: '700px', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setViewingReceipt(null)}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              aria-label="Close"
            >×</button>
            <h3 style={{ marginBottom: '1.25rem' }}>Goods Receipt Details</h3>
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
              <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Receipt #</div>
                  <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{viewingReceipt.receipt_number}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Status</div>
                  <div>{getStatusBadge(viewingReceipt.status)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Warehouse</div>
                  <div style={{ fontWeight: 500 }}>{warehouses.find(w => w.id === viewingReceipt.warehouse_id)?.name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Date</div>
                  <div style={{ fontWeight: 500 }}>{new Date(viewingReceipt.receipt_date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Supplier</div>
                  <div style={{ fontWeight: 500 }}>{viewingReceipt.supplier_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Reference</div>
                  <div style={{ fontWeight: 500 }}>{viewingReceipt.supplier_reference || '-'}</div>
                </div>
              </div>
            </div>

            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '14px' }}>Items</h4>
            <table className="table" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Expected</th>
                  <th>Received</th>
                  <th>Damaged/Short</th>
                </tr>
              </thead>
              <tbody>
                {viewingReceipt.items?.map((item, idx) => {
                  const product = products.find(p => p.id === item.product_id);
                  return (
                  <tr key={idx}>
                    <td>{product ? getProductLabel(product) : `Product ${item.product_id}`}</td>
                    <td>{item.expected_quantity}</td>
                    <td>{item.received_quantity}</td>
                    <td>{item.damaged_quantity}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Related GRNs */}
            {(() => {
              const rootId = viewingReceipt.parent_grn_id || viewingReceipt.id;
              const relatedGrns = receipts.filter(r => 
                (r.id === rootId || r.parent_grn_id === rootId) && r.id !== viewingReceipt.id
              );
              if (relatedGrns.length === 0) return null;
              return (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ fontSize: '14px', marginBottom: '0.75rem' }}>Related GRNs</h4>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                    {relatedGrns.map(r => (
                      <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                        <div>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0d9488' }}>{r.receipt_number}</span>
                          {r.parent_grn_id ? <span style={{ marginLeft: '8px', fontSize: '11px', color: '#64748b' }}>(Follow-up)</span> : <span style={{ marginLeft: '8px', fontSize: '11px', color: '#64748b' }}>(Original)</span>}
                        </div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>{new Date(r.receipt_date).toLocaleDateString('en-IN')}</span>
                          {getStatusBadge(r.status)}
                          <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '2px 8px' }} onClick={() => handleView(r.id)}>View</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
              {isPrivileged && (
                <button className="btn btn-primary" onClick={() => handleEditGrn(viewingReceipt)}>
                  Edit GRN
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setViewingReceipt(null)} style={{ marginLeft: 'auto' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {editingReceipt && isPrivileged && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '1200px', width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
            <h3>Edit Goods Receipt - {editingReceipt.receipt_number}</h3>
            
            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1.5rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Warehouse *</label>
                <select
                  value={editFormData.warehouse_id}
                  onChange={(e) => setEditFormData({ ...editFormData, warehouse_id: parseInt(e.target.value) })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                >
                  <option value={0}>Select Warehouse</option>
                  {warehouses.map(wh => (
                    <option key={wh.id} value={wh.id}>{wh.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Receipt Date</label>
                <input
                  type="date"
                  value={editFormData.receipt_date}
                  onChange={(e) => setEditFormData({ ...editFormData, receipt_date: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
            </div>

            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Supplier Name *</label>
                <input
                  type="text"
                  value={editFormData.supplier_name}
                  onChange={(e) => setEditFormData({ ...editFormData, supplier_name: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Supplier Reference</label>
                <input
                  type="text"
                  value={editFormData.supplier_reference}
                  onChange={(e) => setEditFormData({ ...editFormData, supplier_reference: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <label style={{ fontWeight: 600, fontSize: '16px' }}>Items</label>
              </div>

              <div className="line-items-scroll"><div>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', gap: '0.75rem', padding: '0.75rem', background: '#f1f5f9', borderRadius: '4px', marginBottom: '0.5rem', fontWeight: 600, fontSize: '13px', color: '#475569' }}>
                <div>Product</div>
                <div>Expected</div>
                <div>Received</div>
                <div>Damaged/Short</div>
              </div>

              {editFormData.items.map((item, index) => (
                <div key={index} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '4px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Type SKU or product name..."
                    value={editItemSearchTexts[index] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const newTexts = [...editItemSearchTexts];
                      newTexts[index] = val;
                      setEditItemSearchTexts(newTexts);
                      const match = products.find(p => getProductLabel(p) === val || p.name === val || p.sku === val);
                      if (match) {
                        const newItems = [...editFormData.items];
                        newItems[index] = { ...newItems[index], product_id: match.id };
                        setEditFormData({ ...editFormData, items: newItems });
                        newTexts[index] = getProductLabel(match);
                        setEditItemSearchTexts([...newTexts]);
                      }
                    }}
                    list={`edit-grn-products-${index}`}
                    style={{ padding: '6px 10px', border: '1px solid #dde2e8', borderRadius: '4px', fontSize: '13px' }}
                  />
                  <datalist id={`edit-grn-products-${index}`}>
                    {products.map(p => <option key={p.id} value={getProductLabel(p)}>{getProductLabel(p)}</option>)}
                  </datalist>
                  <input type="number" min="0" value={item.expected_quantity || ''} onChange={(e) => { const newItems = [...editFormData.items]; newItems[index] = { ...newItems[index], expected_quantity: parseInt(e.target.value) || 0 }; setEditFormData({ ...editFormData, items: newItems }); }} disabled={userRole !== 'admin'} style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px', background: userRole !== 'admin' ? '#f1f5f9' : 'white' }} />
                  <input type="number" min="0" value={item.received_quantity || ''} onChange={(e) => { const newItems = [...editFormData.items]; newItems[index] = { ...newItems[index], received_quantity: parseInt(e.target.value) || 0 }; setEditFormData({ ...editFormData, items: newItems }); }} style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px' }} />
                  <input type="number" min="0" value={item.damaged_quantity || ''} onChange={(e) => { const newItems = [...editFormData.items]; newItems[index] = { ...newItems[index], damaged_quantity: parseInt(e.target.value) || 0 }; setEditFormData({ ...editFormData, items: newItems }); }} style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px' }} />
                </div>
              ))}
              </div></div>
            </div>

            <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '1.5rem' }}>
              <button className="btn btn-primary" onClick={handleEditSubmit} disabled={isEditSubmitting} style={{ opacity: isEditSubmitting ? 0.6 : 1 }}>
                {isEditSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="btn btn-cancel" onClick={() => setEditingReceipt(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Goods Receipt"
        message="Are you sure you want to delete this goods receipt? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default GoodsReceiptManager;
